import { useState } from 'react';
import {
    FaPaperPlane, FaEnvelope, FaMapMarkerAlt, FaLinkedin,
    FaGithub, FaInstagram, FaUser, FaCheck, FaExclamationTriangle
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';
import { fadeIn, fadeInUp, staggerContainer } from '../utils/animations';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });

    const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errors, setErrors] = useState<{[key: string]: string}>({});
    const [touched, setTouched] = useState<{[key: string]: boolean}>({});
    const { ref, isInView } = useInView(0.1);

    const validateField = (name: string, value: string) => {
        let error = '';

        switch(name) {
            case 'name':
                if (!value.trim()) error = 'Name is required';
                else if (value.trim().length < 2) error = 'Name must be at least 2 characters';
                break;
            case 'email':
                if (!value.trim()) error = 'Email is required';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Please enter a valid email address';
                break;
            case 'subject':
                if (!value.trim()) error = 'Subject is required';
                break;
            case 'message':
                if (!value.trim()) error = 'Message is required';
                else if (value.trim().length < 10) error = 'Message must be at least 10 characters';
                break;
            default:
                break;
        }

        return error;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (touched[name]) {
            const error = validateField(name, value);
            setErrors(prev => ({ ...prev, [name]: error }));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));

        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const validateForm = () => {
        const newErrors: {[key: string]: string} = {};
        let isValid = true;
        Object.entries(formData).forEach(([name, value]) => {
            const error = validateField(name, value as string);
            if (error) {
                newErrors[name] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const allTouched = Object.keys(formData).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {} as {[key: string]: boolean});

        setTouched(allTouched);

        if (!validateForm()) return;

        setFormStatus('submitting');

        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const subject = encodeURIComponent(formData.subject || "Contact from Portfolio");
            const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage: ${formData.message}`);
            window.location.href = `mailto:feresbenbrahim01@gmail.com?subject=${subject}&body=${body}`;
            setFormData({ name: '', email: '', subject: '', message: '' });
            setFormStatus('success');
            setTouched({});
            setTimeout(() => {
                setFormStatus('idle');
            }, 3000);
        } catch (error) {
            console.error('Error submitting form:', error);
            setFormStatus('error');
            setTimeout(() => {
                setFormStatus('idle');
            }, 3000);
        }
    };

    return (
        <motion.section
            id="contact"
            className="py-24 relative"
            ref={ref as any}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            <div className="absolute top-1/4 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>
            <div className="absolute bottom-1/4 left-0 w-1/3 h-1/3 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>

            <div className="responsive-container">
                <motion.div 
                    className="text-center mb-16"
                    variants={fadeInUp}
                >
                    <motion.span
                        className="inline-block px-4 py-1 glass rounded-full text-gray-300 mb-3 text-sm"
                        variants={fadeInUp}
                    >
                        {t('contact.subtitle')}
                    </motion.span>
                    <motion.h2
                        className="section-heading"
                        variants={fadeInUp}
                    >
                        {t('contact.title')}
                    </motion.h2>
                    <motion.div
                        className="h-1 w-20 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full mb-8"
                        variants={fadeInUp}
                    ></motion.div>
                    <motion.p
                        className="section-subheading"
                        variants={fadeInUp}
                    >
                        {t('contact.description')}
                    </motion.p>
                </motion.div>

                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-5 gap-8"
                    variants={staggerContainer}
                >
                    <motion.div
                        className="md:col-span-2"
                        variants={fadeInUp}
                    >
                        <div className="glass-card p-8 h-full">
                            <h3 className="text-2xl font-bold text-white mb-6 gradient-text">{t('contact.letsConnect')}</h3>

                            <div className="space-y-6 mb-8">
                                <div className="flex items-start">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center mr-4 flex-shrink-0">
                                        <FaEnvelope className="text-primary-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-white mb-1">Email</h4>
                                        <a
                                            href="mailto:feresbenbrahim01@gmail.com"
                                            className="text-gray-300 hover:text-primary-400 transition-colors duration-300"
                                        >
                                            feresbenbrahim01@gmail.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="w-12 h-12 rounded-full glass flex items-center justify-center mr-4 flex-shrink-0">
                                        <FaMapMarkerAlt className="text-accent-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-white mb-1">Location</h4>
                                        <p className="text-gray-300">Sousse, Tunisia</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-lg font-medium text-white mb-4">{t('contact.connectWithMe')}</h4>
                                <div className="flex space-x-4">
                                    <a
                                        href="https://linkedin.com/in/feres-ben-brahim-08a454216"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:-translate-y-2"
                                    >
                                        <FaLinkedin className="w-5 h-5" />
                                    </a>
                                    <a
                                        href="https://github.com/feresbr1"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:-translate-y-2"
                                    >
                                        <FaGithub className="w-5 h-5" />
                                    </a>
                                    <a
                                        href="https://instagram.com/feresbr_"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded-full glass flex items-center justify-center text-gray-400 hover:text-white hover:border-primary-500 hover:shadow-glow-sm transition-all duration-300 hover:-translate-y-2"
                                    >
                                        <FaInstagram className="w-5 h-5" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                    <motion.div
                        className="md:col-span-3"
                        variants={fadeInUp}
                    >
                        <div className="glass-card p-8">
                            <h3 className="text-2xl font-bold text-white mb-6">{t('contact.sendMessage')}</h3>
                            {formStatus === 'success' && (
                                <div
                                    className="mb-6 p-4 rounded-lg bg-green-500 bg-opacity-20 border border-green-500 text-green-400 flex items-center animate-fade-in"
                                >
                                    <FaCheck className="mr-2" /> Your message has been sent successfully!
                                </div>
                            )}

                            {formStatus === 'error' && (
                                <div
                                    className="mb-6 p-4 rounded-lg bg-red-500 bg-opacity-20 border border-red-500 text-red-400 flex items-center animate-fade-in"
                                >
                                    <FaExclamationTriangle className="mr-2" /> There was an error sending your message. Please try again.
                                </div>
                            )}

                            <form
                                onSubmit={handleSubmit}
                                className="space-y-6"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="name" className="form-label">
                                            <span className="flex items-center">
                                                <FaUser className="mr-2 text-primary-400" /> {t('contact.form.name')}
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`form-input ${errors.name && touched.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                                                placeholder={t('contact.form.placeholders.name')}
                                            />
                                            {errors.name && touched.name && (
                                                <p className="text-red-400 text-xs mt-1">{errors.name}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="form-label">
                                            <span className="flex items-center">
                                                <FaEnvelope className="mr-2 text-primary-400" /> {t('contact.form.email')}
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={`form-input ${errors.email && touched.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                                                placeholder={t('contact.form.placeholders.email')}
                                            />
                                            {errors.email && touched.email && (
                                                <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="subject" className="form-label">{t('contact.form.subject')}</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`form-input ${errors.subject && touched.subject ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            placeholder={t('contact.form.placeholders.subject')}
                                        />
                                        {errors.subject && touched.subject && (
                                            <p className="text-red-400 text-xs mt-1">{errors.subject}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="message" className="form-label">{t('contact.form.message')}</label>
                                    <div className="relative">
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            rows={5}
                                            className={`form-input ${errors.message && touched.message ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            placeholder={t('contact.form.placeholders.message')}
                                        ></textarea>
                                        {errors.message && touched.message && (
                                            <p className="text-red-400 text-xs mt-1">{errors.message}</p>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="form-button hover:scale-[1.02] active:scale-[0.98] transition-transform"
                                    disabled={formStatus === 'submitting'}
                                >
                                    <span className="flex items-center justify-center">
                                        {formStatus === 'submitting' ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('contact.form.sending')}
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane className="mr-2" />
                                                {t('contact.form.send')}
                                            </>
                                        )}
                                    </span>
                                </button>
                            </form>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.section>
    );
};

export default Contact;
